import { SequentialAnimation } from "@/SequentialAnimation";
import ToggleNoTextSwitch from "@/ToggleNonTextSwitch";
import useProductManagementSchool from "^/business/myPage/useProductManagementSchool";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { BusinessCompanyProduct, BusinessProductGroup } from "~/types/common";

// 상품 그룹 순서 정의 (중요!)
const PRODUCT_GROUP_ORDER = [
  "PRODUCT_GROUP_CLASS_FEE",
  "PRODUCT_GROUP_ADDITIONAL_SERVICE_FEE",
] as const;

// 상품 그룹별 매핑
const PRODUCT_GROUP_MAPPING = {
  PRODUCT_GROUP_001: "수업 이용료 설정",
  PRODUCT_GROUP_002: "기타 서비스 이용료 설정",
  PRODUCT_GROUP_003: "부가 서비스 이용료 설정",
  PRODUCT_GROUP_004: "미용 비용(가위컷)",
  PRODUCT_GROUP_005: "미용 비용(바리깡)",
} as const;

const PRODUCT_GROUP_DESCRIPTIONS = {
  PRODUCT_GROUP_CLASS_FEE:
    "수업 이용요금은 1회 (1일) 기준입니다.\n보호자가 여러 날짜를 일괄 예약 할 경우에는 자동 합산되어 보호자에게 표시됩니다.",
  PRODUCT_GROUP_002:
    "유치원 수업 외 서비스 이용료는 1회 기준이며 단위는 아래 각 항목에서 표시합니다.\n보호자가 여러 날짜를 선택할 경우 자동 합산되어 보호자에게 표시됩니다.",
  PRODUCT_GROUP_ADDITIONAL_SERVICE_FEE:
    "부가서비스는 수업 또는 기타 서비스와 함께 표시되는 서비스 요금입니다.\n각 단위는 아래 각 항목에서 표시합니다.",
  PRODUCT_GROUP_004: "미용 서비스 이용료는 1회 기준입니다.",
  PRODUCT_GROUP_005: "미용 서비스 이용료는 1회 기준입니다.",
} as const;

export interface ProductSchoolRef {
  saveProducts: () => Promise<{
    success: boolean;
    message: string;
    changedCount?: number;
  }>;
  getChangedProducts: () => BusinessCompanyProduct[];
}

const ProductSchool = forwardRef<ProductSchoolRef>((_, ref) => {
  const {
    productList,
    isLoading,
    handleToggle,
    handleInput,
    getChangedProducts,
    saveProducts,
  } = useProductManagementSchool();
  
  // 상품 그룹을 정의된 순서대로 정렬
  const sortedProductList = useMemo(() => {
    if (!productList || productList.length === 0) return [];
    console.log(productList)
    // 정의된 순서에 따라 정렬  
    const sorted = [...productList].sort((a, b) => {
      const indexA = PRODUCT_GROUP_ORDER.indexOf(
        a.cdProductGroup as (typeof PRODUCT_GROUP_ORDER)[number]
      );
      const indexB = PRODUCT_GROUP_ORDER.indexOf(
        b.cdProductGroup as (typeof PRODUCT_GROUP_ORDER)[number]
      );

      // 정의된 순서에 있는 경우 해당 순서 사용
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // 정의된 순서에 없는 경우 문자열 정렬
      if (indexA === -1 && indexB === -1) {
        return a.cdProductGroup.localeCompare(b.cdProductGroup);
      }

      // 정의된 순서에 있는 것을 앞에
      return indexA !== -1 ? -1 : 1;
    });

    console.log(
      "정렬 전 productList:",
      productList.map((p) => p.cdProductGroup)
    );
    console.log(
      "정렬 후 sortedProductList:",
      sorted.map((p) => p.cdProductGroup)
    );

    return sorted;
  }, [productList]);

  // 표시용: 천단위 콤마
  const getDisplayValue = (amount: number) => {
    if (amount === 0) return "";
    return amount.toLocaleString();
  };

  // ref로 외부에서 접근할 수 있는 함수들 노출
  useImperativeHandle(
    ref,
    () => ({
      saveProducts,
      getChangedProducts,
    }),
    [saveProducts, getChangedProducts]
  );

  console.log("변경된 상품들:", getChangedProducts());

  const renderProductGroup = (groupData: BusinessProductGroup) => {
    const groupTitle =
      PRODUCT_GROUP_MAPPING[
        groupData.cdProductGroup as keyof typeof PRODUCT_GROUP_MAPPING
      ] || groupData.productGroupName;
    const groupDescription =
      PRODUCT_GROUP_DESCRIPTIONS[
        groupData.cdProductGroup as keyof typeof PRODUCT_GROUP_DESCRIPTIONS
      ] || "";

    return (
      <div key={groupData.cdProductGroup} className="mt-2 mb-6">
        <div className="font-bold mb-2 text-[16px]">{groupTitle}</div>
        {groupDescription && (
          <div className="mb-4 text-xs text-gray-400 text-[12px] text-[#666666]">
            {groupDescription.split("\n").map((line, idx) => (
              <span key={idx}>
                {line}
                {idx < groupDescription.split("\n").length - 1 && <br />}
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[15%_20%_20%_33%_10%] gap-x-1.5 gap-y-2">
          {groupData.products.map((product: BusinessCompanyProduct) => {
            const isActive = product.activeYn === "Y";

            return (
              <div key={product.seq} className="contents">
                <div className="flex justify-center items-center">
                  <ToggleNoTextSwitch
                    checked={isActive}
                    onChange={() => handleToggle(product.seq)}
                  />
                </div>
                <div className="flex items-center text-[14px] font-semibold">
                  {product.productName}
                </div>
                <div className="flex items-center text-[14px] font-semibold">
                  {product.description}
                </div>
                <input
                  type="text"
                  className="px-2 py-1 w-full text-right rounded border"
                  value={getDisplayValue(product.amount)}
                  onChange={(e) =>
                    handleInput(product.seq, e.target.value)
                  }
                  disabled={!isActive}
                  placeholder=""
                  inputMode="numeric"
                />
                <div className="flex items-center text-[14px] font-semibold">
                  원
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (isLoading || !productList || productList.length === 0) {
    return (
      <SequentialAnimation>
        <div className="p-4 mx-auto rounded-lg">
          <div className="text-center text-gray-500">
            상품 정보를 불러오는 중...
          </div>
        </div>
      </SequentialAnimation>
    );
  }

  return (
    <SequentialAnimation>
      <div className="p-4 mx-auto rounded-lg">
        {sortedProductList.map((groupData) => renderProductGroup(groupData))}
      </div>
    </SequentialAnimation>
  );
});

ProductSchool.displayName = "ProductSchool";

export default ProductSchool;
